import { Component } from '@angular/core';
import { Language, EmailTranslationDto, EmailType } from '../../shared/model';
import { SharedService } from '../../shared/shared.service';
import { SpinnerService } from '../../shared/spinner/spinner.service';
import { EmailTranslationsViewService } from './email-translations-view.service';
import { TranslatedNotificationService } from '../../shared/translation/translated-notification.service';

@Component({
  selector: 'app-email-translations-view',
  templateUrl: './email-translations-view.component.html',
  styleUrls: [
    './email-translations-view.component.css',
    '../admin-grid.css',
    '../../shared/styles/path-selection.css',
    '../../shared/styles/small-navigation.css']
})
export class EmailTranslationsViewComponent {

  languages: Language[] = [];
  selectedLanguage: Language = <Language>{};
  createAppointmentTranslation: EmailTranslationDto;
  cancelAppointmentTranslation: EmailTranslationDto;
  updateAppointmentTranslation: EmailTranslationDto;
  profileChangeTranslation: EmailTranslationDto;
  availabilityChangeTranslation: EmailTranslationDto;
  newAvailabilityTranslation: EmailTranslationDto;
  removedAvailabilityTranslation: EmailTranslationDto;

  constructor(
    private emailTranslationsService: EmailTranslationsViewService,
    private notificationService: TranslatedNotificationService,
    private spinner: SpinnerService,
    private sharedService: SharedService) {
      this.fetchLanguages();
      this.selectedLanguage = this.sharedService.getCurrentLanguage();

      this.clearCreateAppointmentTranslation();
      this.clearCancelAppointmentTranslation();
      this.clearUpdateAppointmentTranslation();
      this.clearProfileChangeTranslation();
      this.clearAvailabilityChangeTranslation();
      this.clearNewAvailabilityTranslation();
      this.clearRemovedAvailabilityTranslation();
      this.fetchEmailTranslationsForLanguage();
  }

  clearCreateAppointmentTranslation() {
    this.createAppointmentTranslation = <EmailTranslationDto>{
      LanguageId: '',
      EmailType: EmailType.CreateAppointment,
      Text: '',
      Title: ''
    };
  }

  clearCancelAppointmentTranslation() {
    this.cancelAppointmentTranslation = <EmailTranslationDto>{
      LanguageId: '',
      EmailType: EmailType.CancelAppointment,
      Text: '',
      Title: ''
    };
  }

  clearUpdateAppointmentTranslation() {
    this.updateAppointmentTranslation = <EmailTranslationDto>{
      LanguageId: '',
      EmailType: EmailType.UpdateAppointment,
      Text: '',
      Title: ''
    };
  }

  clearProfileChangeTranslation() {
    this.profileChangeTranslation = <EmailTranslationDto>{
      LanguageId: '',
      EmailType: EmailType.ProfileChange,
      Text: '',
      Title: ''
    };
  }

  clearAvailabilityChangeTranslation() {
    this.availabilityChangeTranslation = <EmailTranslationDto>{
      LanguageId: '',
      EmailType: EmailType.AvailabilityChange,
      Text: '',
      Title: ''
    };
  }

  clearNewAvailabilityTranslation() {
    this.newAvailabilityTranslation = <EmailTranslationDto>{
      LanguageId: '',
      EmailType: EmailType.NewAvailability,
      Text: '',
      Title: ''
    };
  }

  clearRemovedAvailabilityTranslation() {
    this.removedAvailabilityTranslation = <EmailTranslationDto>{
      LanguageId: '',
      EmailType: EmailType.RemovedAvailability,
      Text: '',
      Title: ''
    };
  }

  fetchLanguages() {
    this.spinner.show('spinner');
    this.sharedService.getLanguages()
      .subscribe((languages) => {
        this.languages = languages;
        this.spinner.hide('spinner');
      },
      () => {
        this.spinner.hide('spinner');
        this.notificationService.showMsgError('error.loading_languages', 'Error while loading languages.');
      });
  }

  fetchEmailTranslationsForLanguage() {
    this.spinner.show('spinner');
    this.emailTranslationsService.getEmailTranslationsForLanguage(this.selectedLanguage.LanguageCode)
    .subscribe((translations: EmailTranslationDto[]) => {
      const createApp = translations.find(e => e.EmailType === EmailType.CreateAppointment);
      createApp ? this.createAppointmentTranslation = createApp : this.clearCreateAppointmentTranslation();
      const cancelApp = translations.find(e => e.EmailType === EmailType.CancelAppointment);
      cancelApp ? this.cancelAppointmentTranslation = cancelApp : this.clearCancelAppointmentTranslation();
      const updateApp = translations.find(e => e.EmailType === EmailType.UpdateAppointment);
      updateApp ? this.updateAppointmentTranslation = updateApp : this.clearUpdateAppointmentTranslation();
      const changeProfile = translations.find(e => e.EmailType === EmailType.ProfileChange);
      changeProfile ? this.profileChangeTranslation = changeProfile : this.clearProfileChangeTranslation();
      const availabilityChange = translations.find(e => e.EmailType === EmailType.AvailabilityChange);
      availabilityChange ? this.availabilityChangeTranslation = availabilityChange : this.clearAvailabilityChangeTranslation();
      const newAvailability = translations.find(e => e.EmailType === EmailType.NewAvailability);
      newAvailability ? this.newAvailabilityTranslation = newAvailability : this.clearNewAvailabilityTranslation();
      const removedAvailability = translations.find(e => e.EmailType === EmailType.RemovedAvailability);
      removedAvailability ? this.removedAvailabilityTranslation = removedAvailability : this.clearRemovedAvailabilityTranslation();
      this.spinner.hide('spinner');
    },
    () => {
      this.spinner.hide('spinner');
      this.notificationService.showMsgError('error.loading_email_translations', 'Error while loading email translations.');
    });
  }

  languageChange(event) {
    this.selectedLanguage = <Language>{
      Id: event.Id,
      Name: event.Name,
      LanguageCode: event.LanguageCode
    };
    this.fetchEmailTranslationsForLanguage();
  }

  languageSelected(): boolean {
    return this.selectedLanguage.Id !== undefined;
  }

  saveChanges() {
    this.spinner.show('spinner');
    this.createAppointmentTranslation.LanguageId = this.selectedLanguage.Id;
    this.updateAppointmentTranslation.LanguageId = this.selectedLanguage.Id;
    this.cancelAppointmentTranslation.LanguageId = this.selectedLanguage.Id;
    this.profileChangeTranslation.LanguageId = this.selectedLanguage.Id;
    this.availabilityChangeTranslation.LanguageId = this.selectedLanguage.Id;
    this.newAvailabilityTranslation.LanguageId = this.selectedLanguage.Id;
    this.removedAvailabilityTranslation.LanguageId = this.selectedLanguage.Id;
    this.emailTranslationsService.saveEmailTranslations(
      [this.createAppointmentTranslation, this.cancelAppointmentTranslation,
        this.updateAppointmentTranslation, this.profileChangeTranslation,
        this.availabilityChangeTranslation, this.newAvailabilityTranslation,
        this.removedAvailabilityTranslation])
    .subscribe(() => {
      this.spinner.hide('spinner');
      this.notificationService.showMsgSuccess('success.emails_saved', 'Email translations saved successfully.');
    },
    () => {
      this.spinner.hide('spinner');
      this.notificationService.showMsgError('error.emails_saved', 'Error while saving email translations.');
    });
  }
}
